import React from "react";
import { useChat } from "@ai-sdk/react";
import { Response } from "../components/ai/response";

/**
 * Vercel Chat SDK-like UI (AI Elements-ish) for React (non-Next).
 * Tailwind is scoped by `.tw` via tailwind.config.js: { important: ".tw" }.
 *
 * Endpoint expected:
 * POST /api/chat  { messages: [{ role, content }, ...] }
 * Streaming response recommended.
 */

export default function ChatVercelPage() {
  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    setMessages,
    error,
  } = useChat({
    api: "/api/chat",
    credentials: "include",
  });

  const safeInput = (input ?? "");

  return (
    <div className="tw h-screen w-full bg-zinc-50 text-zinc-900">
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col border-x bg-white">
        {/* Top bar */}
        <div className="flex h-14 items-center justify-between border-b px-4">
          <div className="text-sm font-semibold">Chat</div>

          <button
            type="button"
            onClick={() => setMessages([])}
            className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-zinc-50"
          >
            New chat
          </button>
        </div>

        {/* Conversation */}
        <Conversation>
          {messages.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-6">
              {messages.map((m) => (
                <MessageRow key={m.id} role={m.role}>
                  <MessageBubble role={m.role}>
                    <Response>{m.content}</Response>
                  </MessageBubble>
                </MessageRow>
              ))}

              {isLoading ? (
                <MessageRow role="assistant">
                  <MessageBubble role="assistant">
                    <span className="text-zinc-500">Thinking</span>
                    <span className="ml-1 animate-pulse text-zinc-500">…</span>
                  </MessageBubble>
                </MessageRow>
              ) : null}
            </div>
          )}
        </Conversation>

        {/* Bottom prompt */}
        <div className="border-t bg-white px-4 py-3">
          {error ? (
            <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {String(error?.message || error)}
            </div>
          ) : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}
            className="flex items-end gap-2"
          >
            <PromptTextarea
              value={input}
              onChange={handleInputChange}
              disabled={isLoading}
              onEnterSend={(e) => handleSubmit(e)}
            />

            <button
              type="submit"
              disabled={isLoading || !safeInput.trim()}
              className="h-10 shrink-0 rounded-xl bg-black px-4 text-sm font-semibold text-white disabled:opacity-50"
            >
              Send
            </button>
          </form>

          <div className="mt-2 text-[11px] text-zinc-500">
            Enter untuk kirim · Shift+Enter untuk baris baru
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- UI pieces (AI Elements-like) ---------------- */

function Conversation({ children }) {
  return (
    <div className="flex-1 overflow-y-auto bg-zinc-50 px-4 py-6">
      {children}
      <AutoScrollAnchor />
    </div>
  );
}

function AutoScrollAnchor() {
  const ref = React.useRef(null);

  React.useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  });

  return <div ref={ref} />;
}

function EmptyState() {
  return (
    <div className="mx-auto mt-16 max-w-md text-center">
      <div className="text-sm font-semibold">Start a conversation</div>
      <div className="mt-2 text-sm text-zinc-500">
        Messages will appear here as the conversation progresses.
      </div>
    </div>
  );
}

function MessageRow({ role, children }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      {children}
    </div>
  );
}

function MessageBubble({ role, children }) {
  const isUser = role === "user";
  return (
    <div
      className={[
        "max-w-[42rem] rounded-2xl px-4 py-2 text-sm leading-relaxed shadow-sm",
        isUser
          ? "bg-black text-white"
          : "border bg-white text-zinc-900",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function PromptTextarea({ value, onChange, disabled, onEnterSend }) {
  const safeValue = value ?? "";

  return (
    <textarea
      value={safeValue}
      onChange={onChange}
      disabled={disabled}
      placeholder="Send a message..."
      rows={1}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (safeValue.trim() && !disabled) onEnterSend(e);
        }
      }}
      className={[
        "h-10 flex-1 resize-none rounded-xl border px-4 py-2 text-sm",
        "outline-none focus:ring-2 focus:ring-black",
        "disabled:bg-zinc-50",
      ].join(" ")}
    />
  );
}

